import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useLazyGetRecordPropertiesQuery,
  useSetRecordPropertiesMutation,
} from '@app/api/Records/api';
import type {
  ModelRecordProperties,
  ModelRecordTypeValue,
} from '@app/api/Records/types';
import type { ModelTag } from '@app/api/Tags';
import {
  CHANNEL_TAG_NAMES,
  CLIENT_TYPES,
  PRICE_TAG_NAMES,
} from '@app/components/RecordModal/components/RecordTagsModal/data';
import { useRecordAfterSave } from '@app/components/RecordModal/hooks/useRecordAfterSave';
import { closeRecordTagsWindow } from '@app/components/RecordModal/model/recordModalSlice';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useAppDispatch, useAppSelector } from '@app/store';

interface ClientTypeOption {
  value: string;
  label: string;
}

interface UseRecordTagsModalResult {
  isOpen: boolean;
  isLoading: boolean;
  isSaving: boolean;
  clientTypes: ReadonlyArray<ClientTypeOption>;
  selectedClientType: string | null;
  recordTypes: ModelRecordTypeValue[];
  selectedRecordTypeKey: string | null;
  channelTags: ModelTag[];
  priceTags: ModelTag[];
  selectedTagIds: number[];
  toggleTag(tagId: number, checked: boolean): void;
  skip(): Promise<void>;
  saveAndContinue(): Promise<void>;
}

const byNames = (tags: ModelTag[], names: readonly string[]): ModelTag[] => {
  const allowed = new Set<string>(names);

  return tags.filter(
    ({ Name }) => Boolean(Name) && allowed.has(Name as string)
  );
};

// Окно тегов после сохранения новой записи (ТЗ us/11144): тип клиента и тип
// записи считает бэк и менеджер их только сверяет, канал обращения и
// стоимость выбирает сам. «Пропустить» сохраняет рассчитанные типы без
// выбранных тегов, «Сохранить и продолжить» — вместе с ними; саму запись окно
// не отменяет, она уже создана.
export const useRecordTagsModal = (): UseRecordTagsModalResult => {
  const dispatch = useAppDispatch();
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const recordId = useAppSelector(
    ({ recordModal }) => recordModal.recordTagsWindowRecordId
  );
  const recordDate = useAppSelector(
    ({ recordModal }) => recordModal.recordTagsWindowDate
  );

  const { afterSave } = useRecordAfterSave();

  const [getRecordProperties, { isFetching }] =
    useLazyGetRecordPropertiesQuery();
  const [setRecordProperties, { isLoading: isSaving }] =
    useSetRecordPropertiesMutation();

  const [properties, setProperties] = useState<ModelRecordProperties | null>(
    null
  );
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const isOpen = recordId !== null;

  useEffect(() => {
    if (recordId === null) {
      setProperties(null);
      setSelectedTagIds([]);
      return;
    }

    const loadProperties = async () => {
      try {
        const response = await getRecordProperties({
          ActiveBranchId: activeBranchId,
          recordId,
        }).unwrap();

        setProperties(response);
        setSelectedTagIds(
          (response.Tags ?? [])
            .filter(({ IsSelected }) => IsSelected)
            .map(({ Id }) => Id)
        );
      } catch (error) {
        showNotification(error);
        dispatch(closeRecordTagsWindow());
      }
    };

    void loadProperties();
  }, [
    activeBranchId,
    dispatch,
    getRecordProperties,
    recordId,
    showNotification,
  ]);

  const tags = useMemo(() => properties?.Tags ?? [], [properties?.Tags]);

  const channelTags = useMemo(() => byNames(tags, CHANNEL_TAG_NAMES), [tags]);
  const priceTags = useMemo(() => byNames(tags, PRICE_TAG_NAMES), [tags]);

  const recordTypes = useMemo(
    () => properties?.RecordTypes ?? [],
    [properties?.RecordTypes]
  );

  const selectedRecordTypeKey = useMemo(
    () => recordTypes.find(({ IsSeleted }) => IsSeleted)?.Key ?? null,
    [recordTypes]
  );

  const selectedClientType = properties?.ClientType ?? null;

  // Значение вне контракта (не new/old) показываем отдельной опцией, чтобы
  // рассчитанный бэком тип не пропал из окна.
  const clientTypes = useMemo(() => {
    const isKnown = CLIENT_TYPES.some(
      ({ value }) => value === selectedClientType
    );

    if (!selectedClientType || isKnown) return CLIENT_TYPES;

    return [
      ...CLIENT_TYPES,
      { value: selectedClientType, label: selectedClientType },
    ];
  }, [selectedClientType]);

  const toggleTag = useCallback((tagId: number, checked: boolean) => {
    setSelectedTagIds((prev) =>
      checked ? [...prev, tagId] : prev.filter((id) => id !== tagId)
    );
  }, []);

  // Рассчитанные типы уходят обратно как пришли: менять их менеджер не может.
  // Различие «Пропустить» и «Сохранить и продолжить» — только в тегах.
  const save = useCallback(
    async (tagIds: number[]) => {
      if (!properties) {
        dispatch(closeRecordTagsWindow());
        return;
      }

      try {
        await setRecordProperties({
          ActiveBranchId: activeBranchId,
          data: {
            ...properties,
            Tags: (properties.Tags ?? []).map((tag) => ({
              ...tag,
              IsSelected: tagIds.includes(tag.Id),
            })),
          },
        }).unwrap();
      } catch (error) {
        showNotification(error);
        return;
      }

      dispatch(closeRecordTagsWindow());

      // Журнал обновился ещё при создании записи, до выбора тегов, — без
      // повторного обновления в превью записи тегов не будет (issue/11332).
      if (!recordDate) return;

      try {
        await afterSave(recordDate);
      } catch (error) {
        showNotification(error, WARNING);
      }
    },
    [
      activeBranchId,
      afterSave,
      dispatch,
      properties,
      recordDate,
      setRecordProperties,
      showNotification,
    ]
  );

  const skip = useCallback(() => save([]), [save]);

  const saveAndContinue = useCallback(
    () => save(selectedTagIds),
    [save, selectedTagIds]
  );

  return {
    isOpen,
    isLoading: isFetching,
    isSaving,
    clientTypes,
    selectedClientType,
    recordTypes,
    selectedRecordTypeKey,
    channelTags,
    priceTags,
    selectedTagIds,
    toggleTag,
    skip,
    saveAndContinue,
  };
};
